import axios, { type AxiosInstance } from "axios";

/**
 * Single shared axios client.
 *
 * - baseURL = VITE_API_URL (e.g. https://staging-api.oyereply.com).
 * - withCredentials sends our session cookie cross-origin (CORS allows
 *   exactly the dashboard origin).
 * - X-CSRF-Token header attached on state-changing methods (POST/PATCH/
 *   DELETE/PUT) — value comes from the AuthProvider after login or
 *   /auth/csrf bootstrap.
 * - 401 responses propagate; the auth context catches and routes to /login.
 *
 * NEVER store the session in JavaScript memory. Cookie is the source of
 * truth; this client just sends it. The CSRF token IS held in memory
 * (per-page) — it's not the session itself, just a value paired with it.
 */

const explicitBaseURL = import.meta.env.VITE_API_URL;

// Localhost fallback ONLY when running `pnpm dev`. A production build
// without VITE_API_URL is a misconfigured deploy — fail loudly so it
// can't get hand-deployed to Cloudflare Pages and silently call the
// developer's machine (Chrome blocks that with LocalNetworkAccessPermissionDenied).
const baseURL = explicitBaseURL
  ? explicitBaseURL
  : import.meta.env.DEV
    ? "http://localhost:3000"
    : (() => {
        const msg =
          "[api] VITE_API_URL is not set in this build. Did the build pick up apps/dashboard/.env.production?";
        // eslint-disable-next-line no-console
        console.error(msg);
        // Throw on the *first* request rather than during module init so
        // dev tooling can still load the file. Sentry will catch.
        return "MISSING_VITE_API_URL";
      })();

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
});

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

const STATE_CHANGING = new Set(["post", "put", "patch", "delete"]);

api.interceptors.request.use((config) => {
  const method = (config.method ?? "get").toLowerCase();
  if (STATE_CHANGING.has(method) && csrfToken) {
    config.headers.set("X-CSRF-Token", csrfToken);
  }
  return config;
});
